<script setup lang="ts">
import { Refresh } from '@element-plus/icons-vue'
import { BoxComponent, ButtonComponent, FlexComponent, InputNumberComponent, TypographyComponent } from '@metapic-apps/shared'
import { ElButton, ElIcon, ElNotification } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { useCreatorCosts } from '@/api/creator-costs.api'
import InstagramIcon from '@/assets/icons/instagram-costs-icon.svg?component'
import OtherIcon from '@/assets/icons/other-costs-icon.svg?component'
import SnapchatIcon from '@/assets/icons/snapchat-costs-icon.svg?component'
import TiktokIcon from '@/assets/icons/tiktok-costs-icon.svg?component'
import DiffText from '@/components/costs/DiffText.vue'
import { useAdvertiserPermissions } from '@/lib/hooks/advertiser-permissions.hooks'
import type { CreatorTrafficSourceCost } from '@/lib/types/creator-costs.types'

const OTHER_SOURCE = 0

const COST_CONFLICT_MESSAGE = 'Either the cpc or cpa field should be 0.'

const SOURCE_ICONS: Record<number, unknown> = {
  0: OtherIcon,
  1: InstagramIcon,
  2: TiktokIcon,
  3: SnapchatIcon,
}

type EditableCost = {
  source: number
  cpc: number | null
  cpa: number | null
}

const props = defineProps<{ creatorId: number }>()

const store = useStore()
const { trafficSources, baseTrafficSources, currencySymbol, isSaving, saveCosts } = useCreatorCosts()
const { advertiserUsesCpa } = useAdvertiserPermissions()

const trafficSourcesTemplates = computed<{ source: number; name: string }[]>(
  () => store.getters['trafficSources/trafficSourcesTemplates'] ?? [],
)

const orderedTemplates = computed(() =>
  [...trafficSourcesTemplates.value].sort((a, b) => {
    if (a.source === b.source) {
      return 0
    }
    if (a.source === OTHER_SOURCE) {
      return 1
    }
    if (b.source === OTHER_SOURCE) {
      return -1
    }
    return a.source - b.source
  }),
)

const editableCosts = ref<EditableCost[]>([])
const originalCosts = ref<EditableCost[]>([])

const buildCosts = (): EditableCost[] => {
  const baseCost = trafficSources.value.find((el) => el.source === OTHER_SOURCE)
  return orderedTemplates.value.map((template) => {
    const existing = trafficSources.value.find((el) => el.source === template.source)
    return {
      source: template.source,
      cpc: existing?.cpc ?? baseCost?.cpc ?? null,
      cpa: existing?.cpa ?? baseCost?.cpa ?? null,
    }
  })
}

const syncFromStore = () => {
  editableCosts.value = buildCosts()
  originalCosts.value = buildCosts()
}

watch([orderedTemplates, trafficSources], syncFromStore, {
  immediate: true,
  deep: true,
})

const hasChanges = computed(() => JSON.stringify(editableCosts.value) !== JSON.stringify(originalCosts.value))

const isSourceInvalid = (cost: EditableCost): boolean => advertiserUsesCpa.value && Number(cost.cpc ?? 0) > 0 && Number(cost.cpa ?? 0) > 0

const hasInvalidCosts = computed(() => editableCosts.value.some(isSourceInvalid))

const baseCostFor = (sourceId: number, field: 'cpc' | 'cpa'): number => {
  const exact = baseTrafficSources.value.find((el) => el.source === sourceId)
  const fallback = baseTrafficSources.value.find((el) => el.source === OTHER_SOURCE)
  return Number(exact?.[field] ?? fallback?.[field] ?? 0)
}

const differsFromDefault = computed(() =>
  editableCosts.value.some((cost) => {
    if (Number(cost.cpc ?? 0) !== baseCostFor(cost.source, 'cpc')) {
      return true
    }
    return advertiserUsesCpa.value && Number(cost.cpa ?? 0) !== baseCostFor(cost.source, 'cpa')
  }),
)

const sourceName = (sourceId: number): string => trafficSourcesTemplates.value.find((el) => el.source === sourceId)?.name ?? ''

const currencyPerClickLabel = computed(() => `${currencySymbol.value}/click`)

const resetToDefault = () => {
  editableCosts.value = editableCosts.value.map((cost) => ({
    source: cost.source,
    cpc: baseCostFor(cost.source, 'cpc'),
    cpa: advertiserUsesCpa.value ? baseCostFor(cost.source, 'cpa') : cost.cpa,
  }))
}

const onSave = async () => {
  if (hasInvalidCosts.value) {
    return
  }
  const payload: CreatorTrafficSourceCost[] = editableCosts.value.map((cost) => ({
    source: cost.source,
    cpc: cost.cpc,
    cpa: cost.cpa,
    invoiceCpc: null,
    invoiceCpa: null,
    type: cost.cpa ? 'CPA' : 'CPC',
  }))
  const success = await saveCosts(props.creatorId, payload)
  if (success) {
    ElNotification.success('The creator costs have been updated')
  } else {
    ElNotification.error({
      message: "Something went wrong and the costs weren't updated",
      duration: 0,
    })
  }
}
</script>

<template>
  <BoxComponent class="commission-card">
    <TypographyComponent variant="headline-small">Commission</TypographyComponent>
    <BoxComponent class="commission-card__section">
      <TypographyComponent variant="title-large">Per click</TypographyComponent>
      <div class="sources">
        <div v-for="cost in editableCosts" :key="`cpc-${cost.source}`" class="source">
          <FlexComponent :gap="8" align="center" justify="center" class="source__header">
            <ElIcon v-if="SOURCE_ICONS[cost.source]" :size="18">
              <component :is="SOURCE_ICONS[cost.source]" />
            </ElIcon>
            <TypographyComponent variant="title-medium">{{ sourceName(cost.source) }}</TypographyComponent>
          </FlexComponent>
          <InputNumberComponent v-model="cost.cpc" controls :min="0" :max="10" :precision="2" :step="0.1" class="source__input" />
          <TypographyComponent variant="label-large">{{ currencyPerClickLabel }}</TypographyComponent>
          <DiffText :cost="cost.cpc" :base-cost="baseCostFor(cost.source, 'cpc')" type="cpc" />
          <TypographyComponent v-if="isSourceInvalid(cost)" variant="body-small" color="#d33131" class="source__error">{{
            COST_CONFLICT_MESSAGE
          }}</TypographyComponent>
        </div>
      </div>
    </BoxComponent>
    <BoxComponent v-if="advertiserUsesCpa" class="commission-card__section">
      <TypographyComponent variant="title-large">Per order</TypographyComponent>
      <div class="sources">
        <div v-for="cost in editableCosts" :key="`cpa-${cost.source}`" class="source">
          <FlexComponent :gap="8" align="center" justify="center" class="source__header">
            <ElIcon v-if="SOURCE_ICONS[cost.source]" :size="18">
              <component :is="SOURCE_ICONS[cost.source]" />
            </ElIcon>
            <TypographyComponent variant="title-medium">{{ sourceName(cost.source) }}</TypographyComponent>
          </FlexComponent>
          <InputNumberComponent v-model="cost.cpa" controls :min="0" :max="100" :precision="0" :step="1" class="source__input" />
          <TypographyComponent variant="label-large">%/order</TypographyComponent>
          <DiffText :cost="cost.cpa" :base-cost="baseCostFor(cost.source, 'cpa')" type="cpa" />
          <TypographyComponent v-if="isSourceInvalid(cost)" variant="body-small" color="#d33131" class="source__error">{{
            COST_CONFLICT_MESSAGE
          }}</TypographyComponent>
        </div>
      </div>
    </BoxComponent>
    <FlexComponent :gap="8" justify="flex-end" align="center" class="commission-card__actions">
      <ElButton v-if="differsFromDefault" text @click="resetToDefault">
        <ElIcon class="reset-icon">
          <Refresh />
        </ElIcon>
        Reset to default
      </ElButton>
      <ButtonComponent variant="accent" :is-disabled="!hasChanges || hasInvalidCosts" :is-loading="isSaving" @click="onSave">
        Save
      </ButtonComponent>
    </FlexComponent>
  </BoxComponent>
</template>

<style scoped lang="scss">
.commission-card {
  padding: 24px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  &__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  &__actions {
    margin-top: 8px;
  }
}
.sources {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}
.source {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  &__input {
    width: 140px;
  }
  &__error {
    text-align: center;
  }
}
.reset-icon {
  margin-right: 4px;
}
</style>
